// Pure number/string formatting helpers for the chart.

/** Format a USD value with sign, compact notation */
export function fmtUsd(v: number | null): string {
    if (v === null) return '—';
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
    return `$${v.toFixed(0)}`;
}

/** Format a USD value with explicit sign prefix (for PnL display) */
export function fmtUsdSigned(n: number): string {
    const sign = n >= 0 ? '+' : '';
    const abs = Math.abs(n);
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(2)}`;
}

/** Format a share count compactly */
export function fmtShares(n: number): string {
    const abs = Math.abs(n);
    if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
    return n.toFixed(1);
}

/** Format a probability as cents (e.g. 0.73 → "73.0¢") */
export function fmtProb(v: number | null): string {
    if (v === null) return '—';
    return `${(v * 100).toFixed(1)}¢`;
}

/** Format a probability as a percentage for axis labels (e.g. 0.51 → "51%") */
export function fmtProbPct(v: number, decimals = 0): string {
    return `${(v * 100).toFixed(decimals)}%`;
}

/** Format a price value for asset overlays */
export function fmtPrice(v: number): string {
    if (v >= 10_000) return `$${(v / 1000).toFixed(0)}k`;
    if (v >= 100) return `$${v.toFixed(0)}`;
    if (v >= 1) return `$${v.toFixed(2)}`;
    return `$${v.toPrecision(2)}`;
}

/** Shorten a wallet address to 6…4 format */
export function shortAddr(addr: string | null): string {
    if (!addr) return '';
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/** Truncate a string with an ellipsis */
export function truncate(str: string, max: number): string {
    return str.length > max ? str.slice(0, max) + '…' : str;
}

/** Format volume for compact display (no $ prefix) */
export function fmtVol(v: number): string {
    if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
    return `$${v.toFixed(0)}`;
}

/** Format a time window; the unit coarsens with the span. */
export function formatRange(range: [Date, Date]): string {
    const spanMs = range[1].getTime() - range[0].getTime();
    const spanDays = spanMs / (1000 * 60 * 60 * 24);
    const fmt =
        spanDays > 365
            ? (d: Date) =>
                d.toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric',
                })
            : spanDays > 2
                ? (d: Date) =>
                    d.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                    })
                : (d: Date) =>
                    d.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    });
    return `${fmt(range[0])} – ${fmt(range[1])}`;
}
